import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';

import { LayerListBloc } from '@connect-services/client/blocs/layer-list.bloc';
import { DeleteEditButtonComponent } from '@connect-services/client/components/delete-edit-button/delete-edit-button.component';
import { ExportSelectionModeEnum } from '@connect-services/client/components/modals/data-export-modal/data-export-modal.component';
import { ButtonComponent, ButtonType } from '@connect-services/client/lib/components/button/button.component';
import { DataExportService } from '@connect-services/client/services/data-export.service';
import { ModeUiSource } from '@connect-services/client/services/edit-mode-layer-panel.service';
import { FeatureService } from '@connect-services/client/services/feature.service';
import { GeographicFilterService } from '@connect-services/client/services/geographic-filter.service';
import {
    FilterModeEnum,
    MapModalSelectionModeEnum,
    SelectionService,
} from '@connect-services/client/services/map/selection/selection.service';
import { SelectionStateService } from '@connect-services/client/services/map/selection/selection-state.service';
import { ActiveGeometryTypeEnum } from '@connect-services/client/services/map-creation-geom.service';
import { MapDrawService } from '@connect-services/client/services/map-draw.service';
import { MapEditModeService } from '@connect-services/client/services/map-edit-mode.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { StandardizedLayerService } from '@connect-services/client/services/standardized-layer.service';
import { ZoneDelimiterService } from '@connect-services/client/services/zone-delimiter.service';
import { SelectComponent, SelectOption } from '@connect-services/src/lib/components/select/select.component';
import { Layer } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';

const ANOTHER_SELECTION_MODE_ACTIVE_TOOLTIP = 'anotherSelectionModeActive';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ButtonComponent, DeleteEditButtonComponent, RessourcesModule, SelectComponent],
    selector: 'app-map-modal-selection',
    standalone: true,
    templateUrl: './map-modal-selection.component.html',
})
export class MapModalSelectionComponent {
    protected readonly canDisplayDefaultSelectionActions = computed<boolean>(() => {
        return (
            !this.selectionService.isFieldInterventionAreaMode() &&
            !this.selectionService.isFieldInterventionSelectionMode()
        );
    });
    protected readonly cannotDrawPolygon = computed<boolean>(
        () =>
            this.selectionService.selectionMode() === FilterModeEnum.SELECT_ZONE &&
            !!this.selectionService.selectedLayerIds().length,
    );
    protected readonly deleteTooltip = computed<string>(() => {
        if (!this.mapEditModeService.editPermissions().canDelete) {
            return 'insufficientPermissions';
        }

        if (!this.selectionStateService.hasSelectedEntities()) {
            return 'editMode.noEntitySelected';
        }

        if (this.hasStandardizedSelectedLayer()) {
            return 'standardizedDataActionUnavailable';
        }

        return 'editMode.deleteSelection';
    });
    protected readonly drawPolygonTooltip = computed<typeof ANOTHER_SELECTION_MODE_ACTIVE_TOOLTIP | 'drawPolygon'>(
        () => (this.cannotDrawPolygon() ? ANOTHER_SELECTION_MODE_ACTIVE_TOOLTIP : 'drawPolygon'),
    );
    protected readonly filteredLayerSelectionOption = computed<SelectOption<string[]>[]>(() => {
        const geometryType =
            this.mapEditModeService.activeGeometryType() ?? this.mapEditModeService.activeEditSelectionGeometryType();

        switch (geometryType) {
            case ActiveGeometryTypeEnum.FILL:
                return this.selectionService.polygonLayerOptions();
            case ActiveGeometryTypeEnum.LINE:
                return this.selectionService.lineStringLayerOptions();
            default:
                return this.selectionService.layersOptions();
        }
    });
    protected readonly hasStandardizedSelectedLayer = computed<boolean>(() => {
        if (
            !this.mapEditModeService.isMapEditModeActive() ||
            !this.selectedLayerIdsAfterApply().length ||
            !this.standardizedLayerIds().size
        ) {
            return false;
        }

        return this.selectedLayerIdsAfterApply().some((layerId: string) => this.standardizedLayerIds().has(layerId));
    });
    protected readonly isDeleteButtonDisabled = computed<boolean>(() => {
        if (!this.mapEditModeService.editPermissions().canDelete || !this.selectionStateService.hasSelectedEntities()) {
            return true;
        }

        return this.hasStandardizedSelectedLayer();
    });
    protected readonly isSelectionApplicable = computed<boolean>(() => {
        return this.canApplySelection();
    });
    protected readonly isSelectionClearable = computed<boolean>(() => {
        const hasDrawnPolygon = this.mapDrawService.hasDrawnPolygon();
        const hasSelectedEntities = this.selectionStateService.hasSelectedEntities();
        const hasExtentSelection = !!this.geographicFilterService.selectedExtentLayersAndEntities()?.layers.length;

        return hasDrawnPolygon || hasSelectedEntities || hasExtentSelection;
    });
    protected readonly layerExtentSelectedDefaultOptions = signal<SelectOption<string[]>[]>([]);
    protected readonly selectPolygonLayerTooltip = computed<
        typeof ANOTHER_SELECTION_MODE_ACTIVE_TOOLTIP | 'selectPolygonLayer'
    >(() => (this.mapDrawService.hasDrawnPolygon() ? ANOTHER_SELECTION_MODE_ACTIVE_TOOLTIP : 'selectPolygonLayer'));
    protected readonly validTooltipKey = computed<string>((): string => {
        if (this.isSelectionApplicable()) {
            return '';
        }

        if (this.selectionService.isFieldInterventionAreaMode()) {
            return 'selectOrDawnOneOrMoreArea';
        }

        return 'selectOneOrMoreLayers';
    });

    // eslint-disable-next-line @typescript-eslint/member-ordering
    protected readonly ButtonType = ButtonType;
    protected readonly dataExportService = inject(DataExportService);
    protected readonly ExportSelectionModeEnum = ExportSelectionModeEnum;
    protected readonly featureService = inject(FeatureService);
    protected readonly FilterModeEnum = FilterModeEnum;
    protected readonly geographicFilterService = inject(GeographicFilterService);
    protected readonly layerListBloc = inject(LayerListBloc);
    protected readonly mapDrawService = inject(MapDrawService);
    protected readonly mapEditModeService = inject(MapEditModeService);
    protected readonly mapModalService = inject(MapModalService);
    protected readonly mapSourceService = inject(MapSourceService);
    protected readonly selectionService = inject(SelectionService);
    protected readonly selectionStateService = inject(SelectionStateService);
    protected readonly sourcesService = inject(SourcesService);

    private readonly selectedLayerIdsAfterApply = computed<string[]>(() => {
        return Object.keys(this.selectionStateService.selectedEntitiesByLayerId());
    });
    private readonly standardizedLayerIds = signal<Set<string>>(new Set<string>());

    // eslint-disable-next-line @typescript-eslint/member-ordering
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly standardizedLayerService = inject(StandardizedLayerService);
    private readonly zoneDelimiterService = inject(ZoneDelimiterService);

    public constructor() {
        if (!this.selectionService.isFieldInterventionAreaMode()) {
            this.selectionService.setModalMode(MapModalSelectionModeEnum.SELECTION);
        }

        this.layerExtentSelectedDefaultOptions.set(this.setLayerExtentSelected());

        effect(() => {
            if (!this.selectionService.isSelectionActive()) {
                this.mapModalService.close(MapModal.SELECTION);
            }
        });

        effect(() => {
            const layers = this.layerListBloc.value()?.items;
            if (!layers?.length) {
                this.standardizedLayerIds.set(new Set<string>());

                return;
            }

            void this.loadStandardizedLayerIds(layers);
        });

        effect(() => {
            const isEditSelectionActive =
                !!this.mapEditModeService.activeEditSelectionGeometryType() &&
                this.mapEditModeService.activeEditSelectionGeometryType() !== ActiveGeometryTypeEnum.SYMBOL;

            this.selectionService.isEditActive.set(isEditSelectionActive);
        });

        effect(() => {
            const isCreationSelectionActive =
                !!this.mapEditModeService.activeGeometryType() &&
                this.mapEditModeService.activeGeometryType() !== ActiveGeometryTypeEnum.SYMBOL;

            this.selectionService.isCreationActive.set(isCreationSelectionActive);
        });
    }

    protected async clearSelection(): Promise<void> {
        await this.selectionService.clearAllSelections(false, ModeUiSource.MAP);
        if (!this.selectionService.isFieldInterventionAreaMode()) {
            return;
        }

        this.selectionService.selectionMode.set(null);
        this.selectionService.selectedLayerIds.set([]);
        this.geographicFilterService.isSelectZoneActive.set(false);
        this.geographicFilterService.selectedExtentLayersAndEntities.set({
            entities: [],
            layers: [],
        });
    }

    protected async onLayerExtentChange(selectedOptions: SelectOption<string[]>[]): Promise<void> {
        if (!selectedOptions[0]) {
            this.selectionService.selectedLayerIds.set([]);
            this.geographicFilterService.selectedExtentLayersAndEntities.set({
                entities: [],
                layers: [],
            });

            await this.selectZoneLayerService.removeLayers();

            return;
        }

        const extentLayerIds: string[] = selectedOptions.map((option) => option.value).flat();

        this.geographicFilterService.selectedExtentLayersAndEntities.set({
            entities: [],
            layers: extentLayerIds,
        });

        this.selectionService.selectedLayerIds.set(extentLayerIds);
        await this.selectZoneLayerService.addSelectedLayerEntities(extentLayerIds);
    }

    protected async toggleLayerSelection(): Promise<void> {
        await this.zoneDelimiterService.resetFilters();

        if (this.selectionService.selectionMode() === FilterModeEnum.SELECT_ZONE) {
            await this.selectionService.clearAllSelections();
            this.selectionService.selectionMode.set(null);
            this.geographicFilterService.isSelectZoneActive.set(false);

            return;
        }

        this.selectionService.selectionMode.set(FilterModeEnum.SELECT_ZONE);
        this.geographicFilterService.isSelectZoneActive.set(true);
        this.mapDrawService.clearDrawnPolygons();
    }

    private canApplySelection(): boolean {
        if (this.selectionService.isFieldInterventionAreaMode()) {
            return this.mapDrawService.hasDrawnPolygon() || !!this.selectionService.selectedLayerIds().length;
        }

        const hasDrawnPolygon = this.mapDrawService.hasDrawnPolygon();
        const hasSelectedLayerIds = !!this.selectionService.selectedLayerIds().length;
        const hasLayersSelectedInStore = !!Object.keys(this.selectionStateService.selectedEntitiesByLayerId()).length;

        if (!hasLayersSelectedInStore) {
            return false;
        }

        return (
            hasDrawnPolygon ||
            hasSelectedLayerIds ||
            (hasLayersSelectedInStore && this.selectionService.isCreationActive())
        );
    }

    private async loadStandardizedLayerIds(layers: Layer[]): Promise<void> {
        this.standardizedLayerIds.set(await this.standardizedLayerService.getStandardizedLayerIds(layers));
    }

    private setLayerExtentSelected(): SelectOption<string[]>[] {
        const selectedLayerIds: string[] = this.geographicFilterService.selectedExtentLayersAndEntities()?.layers ?? [];
        const availableOptions: SelectOption<string[]>[] = this.selectionService.layersOptions();

        if (!selectedLayerIds.length || !availableOptions.length) {
            return [];
        }

        const matchingOption: SelectOption<string[]> | undefined = availableOptions.find(
            (option: SelectOption<string[]>) => {
                return option.value.every((layerId: string) => selectedLayerIds.includes(layerId));
            },
        );

        return matchingOption ? [matchingOption] : [];
    }
}
